import { ResultOf } from "@graphql-typed-document-node/core";
import { uniqBy } from "lodash";
import { useEffect, useState } from "react";
import { CombinedError, useQuery } from "urql";

import { getFragmentData, graphql } from "@/gen";

const useSearchPlayers_Player = graphql(`
    fragment useSearchPlayers_Player on Player {
        id
        name
        fideId
        nickname
        imageId
        ...PlayerRow_Player
    }
`);

export type PlayerT = ResultOf<typeof useSearchPlayers_Player>;

const PLAYERS_PAGE_SIZE = 50;

type UseSearchPlayersResult = {
    players: ResultOf<typeof useSearchPlayers_Player>[];
    hasMore: boolean;
    playersCount: number | null;
    pageCounts: number | null;
    loading: boolean;
    error?: CombinedError | null;
    refetch: () => void;
};

export const useSearchPlayers = ({
    query,
    currentPage,
    pause = false,
}: {
    query: string;
    currentPage: number;
    pause?: boolean;
}): UseSearchPlayersResult => {
    const [players, setPlayers] = useState<PlayerT[]>([]);

    const offset = currentPage * PLAYERS_PAGE_SIZE;

    const [result, refetch] = useQuery({
        query: graphql(`
            query searchPlayers($input: SearchPlayersInput!) {
                searchPlayers(input: $input) {
                    items {
                        id
                        ...useSearchPlayers_Player
                    }
                    total
                    hasMore
                }
            }
        `),
        variables: {
            input: {
                query,
                limit: 50,
                offset,
            },
        },
        pause,
    });

    useEffect(() => {
        if (result.data?.searchPlayers?.items) {
            const players = getFragmentData(
                useSearchPlayers_Player,
                result.data.searchPlayers.items
            );
            setPlayers(uniqBy(players ?? [], "id"));
        }
    }, [result.data?.searchPlayers?.items]);

    return {
        players,
        hasMore: result.data?.searchPlayers?.hasMore ?? false,
        playersCount: result.data?.searchPlayers?.total ?? null,
        pageCounts: result.data?.searchPlayers?.total
            ? Math.ceil(result.data.searchPlayers.total / 50)
            : null,
        loading: result.fetching,
        error: result.error,
        refetch,
    };
};
